import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../../context/ThemeContext';

/**
 * PublicProfileLink component provides a link for users to view their public profile
 * Shows how their profile appears to other users on the platform
 */
const PublicProfileLink = ({ 
  userId, 
  className = "",
  variant = "button", // "button" or "link"
  onError = null // Optional error callback
}) => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [isNavigating, setIsNavigating] = useState(false);
  const [error, setError] = useState(null);

  const handleClick = async (e) => {
    e.preventDefault();
    
    if (!userId) {
      const errorMessage = 'PublicProfileLink: userId is required';
      console.error(errorMessage);
      setError('Unable to navigate to profile');
      if (onError) {
        onError(new Error(errorMessage));
      }
      return;
    }

    try {
      setIsNavigating(true);
      setError(null);
      
      // Add a small delay to show loading state for better UX
      await new Promise(resolve => setTimeout(resolve, 100));
      
      navigate(`/network/${userId}`, { 
        state: { from: '/profile' } 
      });
    } catch (err) {
      console.error('Error navigating to public profile:', err);
      setError('Failed to navigate to profile');
      if (onError) {
        onError(err);
      }
    } finally {
      setIsNavigating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e);
    }
  };

  // Clear error after a delay
  React.useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (!userId) {
    return null;
  }

  // Show error state if there's an error
  if (error) {
    return (
      <div 
        id="public-profile-error"
        className={`inline-flex items-center px-3 py-2 text-sm text-red-600 dark:text-red-400 ${className}`}
        role="alert"
        aria-live="polite"
      >
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="text-xs">{error}</span>
      </div>
    );
  }

  const baseClasses = `
    inline-flex items-center justify-center
    transition-all duration-200 ease-out
    focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
    dark:focus:ring-offset-gray-800
    cursor-pointer
    ${isNavigating ? 'opacity-75 cursor-wait' : ''}
    ${className}
  `;

  if (variant === "link") {
    return (
      <button
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        disabled={isNavigating}
        className={`
          ${baseClasses}
          text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300
          underline hover:no-underline
          text-sm font-medium
          px-2 py-1 rounded-md
          hover:bg-purple-50 dark:hover:bg-purple-900/20
          disabled:opacity-50 disabled:cursor-not-allowed
        `}
        aria-label={isNavigating ? "Loading public profile" : "View your public profile as others see it"}
        title="See how your profile appears to other users"
        aria-describedby={error ? "public-profile-error" : undefined}
      >
        {isNavigating ? (
          <>
            <svg className="w-4 h-4 mr-1.5 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="hidden sm:inline">Loading...</span>
            <span className="sm:hidden">Loading</span>
          </>
        ) : (
          <>
            <EyeIcon className="w-4 h-4 mr-1.5 shrink-0" aria-hidden="true" />
            <span className="hidden sm:inline">View Public Profile</span>
            <span className="sm:hidden">Public View</span>
          </>
        )}
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={isNavigating}
      className={`
        ${baseClasses}
        px-3 py-2 sm:px-4 sm:py-2
        bg-white dark:bg-gray-800
        border border-gray-300 dark:border-gray-600
        text-gray-700 dark:text-gray-300
        hover:bg-gray-50 dark:hover:bg-gray-700
        hover:border-gray-400 dark:hover:border-gray-500
        hover:shadow-md
        hover:-translate-y-0.5
        rounded-lg
        text-sm font-medium
        min-h-[40px] sm:min-h-[44px]
        shadow-sm
        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:transform-none
        mb-safe-bottom
      `}
      aria-label={isNavigating ? "Loading public profile" : "View your public profile as others see it"}
      title="See how your profile appears to other users"
      aria-describedby={error ? "public-profile-error" : undefined}
    >
      {isNavigating ? (
        <>
          <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="hidden sm:inline">Loading...</span>
          <span className="sm:hidden">Loading</span>
        </>
      ) : (
        <>
          <EyeIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2 shrink-0" aria-hidden="true" />
          <span className="hidden sm:inline">View Public Profile</span>
          <span className="sm:hidden">Public View</span>
        </>
      )}
    </button>
  );
};

export default PublicProfileLink;